"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GraphAttachPoint = void 0;
const rc_js_util_1 = require("rc-js-util");
const e_mouse_event_type_1 = require("../eventing/user-interaction/internal-events/e-mouse-event-type");
const e_pointer_event_type_1 = require("../eventing/user-interaction/internal-events/e-pointer-event-type");
const canvas_dimensions_1 = require("./canvas-dimensions");
const on_canvas_pointer_event_1 = require("./events/on-canvas-pointer-event");
const on_canvas_mouse_event_1 = require("./events/on-canvas-mouse-event");
const on_dpr_changed_1 = require("./events/on-dpr-changed");
/**
 * @public
 * {@inheritDoc IGraphAttachPoint}
 */
class GraphAttachPoint extends rc_js_util_1.AOnDestroy {
    constructor(attachPointProvider, chartEventService, chartConfig, $window = window) {
        super();
        this.chartEventService = chartEventService;
        this.chartConfig = chartConfig;
        this.$window = $window;
        this.canvasDims = canvas_dimensions_1.CanvasDimensions.createDefault();
        this.hiddenElements = {};
        this.chartWrapper = attachPointProvider.getWrapperElement();
        this.hiddenElement = attachPointProvider.getHiddenElement();
        this.canvasElement = attachPointProvider.getCanvasElement();
        this.registerEventHandlers();
    }
    resizeCanvas() {
        DEBUG_MODE && rc_js_util_1._Debug.assert(this.canvasElement.clientWidth > 0 && this.canvasElement.clientHeight > 0, "probably resized while off screen");
        const w = this.canvasElement.clientWidth | 0;
        const h = this.canvasElement.clientHeight | 0;
        const dpr = this.$window.devicePixelRatio;
        if (this.canvasDims.cssDims.getXMax() === w && this.canvasDims.cssDims.getYMax() === h && dpr === this.canvasDims.dpr) {
            return this.canvasDims;
        }
        // update dims
        const boundingRect = this.canvasElement.getBoundingClientRect();
        this.canvasDims = new canvas_dimensions_1.CanvasDimensions(dpr, rc_js_util_1.Range2d.f32.factory.createOne(0, w | 0, 0, h | 0), rc_js_util_1.Range2d.f32.factory.createOne(0, (w * dpr) | 0, 0, (h * dpr) | 0), rc_js_util_1.Range2d.f32.factory.createOne(boundingRect.left, boundingRect.right, boundingRect.top, boundingRect.bottom));
        // synchronize the canvas' dimensions to the css size
        this.canvasElement.width = this.canvasDims.pixelDims.getXRange();
        this.canvasElement.height = this.canvasDims.pixelDims.getYRange();
        return this.canvasDims;
    }
    addHiddenElement(className, tagName) {
        const storedElement = this.hiddenElements[className];
        if (storedElement != null) {
            return storedElement;
        }
        else {
            const element = this.hiddenElements[className] = this.$window.document.createElement(tagName);
            element.classList.add(className);
            this.hiddenElement.appendChild(element);
            return element;
        }
    }
    removeHiddenElement(className) {
        const element = this.hiddenElement.getElementsByClassName(className)[0];
        if (element == null) {
            return;
        }
        this.hiddenElement.removeChild(element);
    }
    registerEventHandlers() {
        if (this.chartConfig.interactionOptions.disableAllInteraction) {
            return;
        }
        this.onDestroyListener.initializeListener(() => {
            const options = { once: true, passive: true };
            let mq = matchMedia(`(resolution: ${this.$window.devicePixelRatio}dppx)`);
            const emitEvent = () => {
                const dpr = this.$window.devicePixelRatio;
                mq = matchMedia(`(resolution: ${dpr}dppx)`);
                mq.addEventListener("change", emitEvent, options);
                on_dpr_changed_1.OnDprChanged.emit(this.chartEventService, dpr);
            };
            mq.addEventListener("change", emitEvent, options);
            return () => mq.removeEventListener("change", emitEvent);
        });
        // mouse events
        if (this.chartConfig.interactionOptions.scrollZooms) {
            this.onDestroyListener.initializeListener(() => {
                const onEvent = ($event) => {
                    $event.stopPropagation();
                    $event.preventDefault();
                    on_canvas_mouse_event_1.OnCanvasMouseEvent.emit(this.chartEventService, e_mouse_event_type_1.EMouseEventType.Wheel, $event);
                };
                this.chartConfig.changeDetectionConfig.runOutsideOfChangeDetection(() => {
                    this.canvasElement.addEventListener("wheel", onEvent, { passive: false });
                });
                return () => this.canvasElement.removeEventListener("wheel", onEvent);
            });
        }
        if (this.chartConfig.interactionOptions.disableLongPressContext) {
            this.onDestroyListener.initializeListener(() => {
                const onEvent = ($event) => {
                    $event.stopPropagation();
                    $event.preventDefault();
                };
                this.chartConfig.changeDetectionConfig.runOutsideOfChangeDetection(() => {
                    this.canvasElement.addEventListener("contextmenu", onEvent, { passive: false });
                });
                return () => this.canvasElement.removeEventListener("contextmenu", onEvent);
            });
        }
        // pointer events
        this.onDestroyListener.initializeListener(() => {
            const onEvent = ($event) => on_canvas_pointer_event_1.OnCanvasPointerEvent.emit(this.chartEventService, e_pointer_event_type_1.EPointerEventType.Out, $event);
            this.chartConfig.changeDetectionConfig.runOutsideOfChangeDetection(() => {
                this.canvasElement.addEventListener("pointerout", onEvent, { passive: true });
            });
            return () => this.canvasElement.removeEventListener("pointerout", onEvent);
        });
        this.onDestroyListener.initializeListener(() => {
            const onEvent = ($event) => on_canvas_pointer_event_1.OnCanvasPointerEvent.emit(this.chartEventService, e_pointer_event_type_1.EPointerEventType.Down, $event);
            this.chartConfig.changeDetectionConfig.runOutsideOfChangeDetection(() => {
                this.canvasElement.addEventListener("pointerdown", onEvent, { passive: true });
            });
            return () => this.canvasElement.removeEventListener("pointerdown", onEvent);
        });
        this.onDestroyListener.initializeListener(() => {
            const onEvent = ($event) => on_canvas_pointer_event_1.OnCanvasPointerEvent.emit(this.chartEventService, e_pointer_event_type_1.EPointerEventType.Up, $event);
            this.chartConfig.changeDetectionConfig.runOutsideOfChangeDetection(() => {
                this.canvasElement.addEventListener("pointerup", onEvent, { passive: true });
            });
            return () => this.canvasElement.removeEventListener("pointerup", onEvent);
        });
        this.onDestroyListener.initializeListener(() => {
            const onEvent = ($event) => on_canvas_pointer_event_1.OnCanvasPointerEvent.emit(this.chartEventService, e_pointer_event_type_1.EPointerEventType.Move, $event);
            this.chartConfig.changeDetectionConfig.runOutsideOfChangeDetection(() => {
                this.canvasElement.addEventListener("pointermove", onEvent, { passive: true });
            });
            return () => this.canvasElement.removeEventListener("pointermove", onEvent);
        });
    }
    updateBoundingRects() {
        const boundingRect = this.canvasElement.getBoundingClientRect();
        this.canvasDims.updateBoundingRects(boundingRect);
    }
}
exports.GraphAttachPoint = GraphAttachPoint;
//# sourceMappingURL=graph-attach-point.js.map